// EA-1123 / EA-1195: shared inspector trade-qualification logic.
//
// Qualifying an inspector for an inspection depends on THREE things, not just
// "inspector trade ∩ inspection trade":
//   1. The inspector's licenses, with the Residential license expanded to the
//      sub-trades it covers (Building/Mechanical/Electrical/Plumbing).
//   2. The inspection's own trade(s).
//   3. The trades valid for the PROJECT TYPE (1&2 dwelling vs multifamily/
//      commercial), resolved server-side via getTradesByProjectType. On a
//      commercial project a bare Residential license is NOT sufficient, and the
//      project-type trade set is what enforces that.
//
// This mirrors the logic in PermitInspectionsForm (EA-1195) so the bulk
// scheduling screens (EA-1123) agree with the single-inspection edit form.

// A Residential license covers these sub-trades — but only for project types
// whose project-type trade set actually includes them (i.e. 1&2 dwelling).
export const TRADE_MAPPING: Record<string, string[]> = {
  Residential: ['Mechanical', 'Electrical', 'Plumbing', 'Building'],
};

const norm = (s: string) => (s || '').trim().toLowerCase();

export interface TradeRef {
  id: number;
  name: string;
}

// Expand an inspector's raw trade ids into the full set of trade ids they are
// effectively qualified for, using the supplied `trades` list (typically the
// project-type trade set) to map ids <-> names and back.
export function getEffectiveTradeIds(
  inspectorTradeIds: Array<number | string> | null | undefined,
  trades: TradeRef[],
): number[] {
  const ids = (inspectorTradeIds || [])
    .map(x => Number(typeof x === 'object' && x !== null ? (x as any).id : x))
    .filter(x => Number.isFinite(x));

  const byId = new Map<number, string>(trades.map(t => [t.id, t.name]));
  const byName = new Map<string, number>(trades.map(t => [norm(t.name), t.id]));

  const tradeNames = new Set<string>();
  ids.forEach(id => {
    const name = byId.get(id);
    if (name) tradeNames.add(name);
  });

  const effectiveNames = new Set<string>(tradeNames);

  Object.entries(TRADE_MAPPING).forEach(([parent, children]) => {
    if (Array.from(tradeNames).some(n => norm(n) === norm(parent))) {
      children.forEach(child => effectiveNames.add(child));
    }
  });

  const effectiveIds = new Set<number>(ids);
  effectiveNames.forEach(name => {
    const id = byName.get(norm(name));
    if (id != null) effectiveIds.add(id);
  });

  return Array.from(effectiveIds);
}

// Returns true when an inspector qualifies for the given inspection trades on a
// project whose valid trade set is `projectTypeTrades`.
//
// An inspector qualifies when their effective trades intersect BOTH the
// inspection's trades AND the project-type trade set. When `projectTypeTrades`
// is empty we don't gate on it (nothing to constrain against).
export function isInspectorQualified(
  inspectorRawTrades: Array<number | string> | null | undefined,
  inspectionTradeIds: Array<number>,
  projectTypeTrades: TradeRef[],
): boolean {
  const inspectionSet = new Set<number>(inspectionTradeIds.map(Number));
  if (inspectionSet.size === 0) return true;

  const effIds = getEffectiveTradeIds(inspectorRawTrades, projectTypeTrades);

  if (projectTypeTrades && projectTypeTrades.length) {
    const projectSet = new Set<number>(projectTypeTrades.map(t => Number(t.id)));
    return effIds.some(id => inspectionSet.has(id) && projectSet.has(id));
  }

  return effIds.some(id => inspectionSet.has(id));
}
